import PropTypes from "prop-types";
import styled from "styled-components";
import { FaPrint, FaShare, FaClock } from "react-icons/fa";
import ContentLayout from "../../../layout/ContentLayout";
import { H2, H3, Paragraph } from "../../../styles/AppStyles";
import { LINKS, href } from "../../../config/links";

const CalloutBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  border-left: 5px solid ${({ $type, theme }) => {
    if ($type === "critical") return "#dc2626";
    if ($type === "warning") return "#f59e0b";
    if ($type === "success") return "#10b981";
    return theme.iconColor;
  }};
  background-color: ${({ $type, theme }) => {
    if ($type === "critical") return "rgba(220, 38, 38, 0.08)";
    if ($type === "warning") return "rgba(245, 158, 11, 0.08)";
    if ($type === "success") return "rgba(16, 185, 129, 0.08)";
    return "rgba(99, 102, 241, 0.08)";
  }};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  strong {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
    color: ${({ $type }) => {
      if ($type === "critical") return "#dc2626";
      if ($type === "warning") return "#f59e0b";
      if ($type === "success") return "#10b981";
      return "#6366f1";
    }};
  }

  ul {
    margin: 0.75rem 0 0 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }
`;

const PhaseBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.5rem;
  background-color: ${({ theme }) => theme.background};
  border: 2px solid ${({ theme }) => theme.iconColor};
  border-radius: 8px;
  color: ${({ theme }) => theme.text};

  h3 {
    margin-top: 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .phase-time {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    color: ${({ theme }) => theme.iconColor};
    margin-bottom: 1rem;
    font-weight: 600;
  }

  ol {
    margin: 0.75rem 0 0 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.75rem;
      line-height: 1.6;
    }
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin: 2rem 0;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;

    button,
    a {
      width: 100%;
    }
  }
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background-color: ${({ theme }) => theme.iconColor};
  color: ${({ theme }) => theme.background};
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const IncidentResponseGuide = ({ istouch, isdarkmode }) => {
  const canonicalUrl = href(LINKS.QUICK_GUIDES.INCIDENT_RESPONSE);

  // const heroImageSrc = "/images/guides/incident-response.webp";

  return (
    <ContentLayout
        istouch={istouch}
        isdarkmode={isdarkmode}
        displayTitle="Quick Incident Response Checklist"
        seoTitle="Incident Response Checklist: First Steps to Take | CSFSafe"
        canonicalUrl={canonicalUrl}
        description="Step-by-step checklist for responding to a cybersecurity incident. Immediate actions, containment, communication, and recovery."
        ogTitle="Incident Response Checklist: First Steps to Take | CSFSafe"
        ogDescription="Know what to do in the first hours of a security incident. Practical checklist for SMBs."
        // ogImage={heroImageSrc}
        // heroImageSrc={heroImageSrc}
        heroImageAlt="Incident response and crisis management"
        publishedLabel="Quick Reference Guide — July 21, 2026"
        showCTA={true}
      >
      <Paragraph>
        When a cybersecurity incident occurs, the first few hours are critical.
        This checklist helps you take the right immediate actions, contain the
        damage, communicate with stakeholders, and begin recovery. Print this
        guide and keep it accessible to your security team.
      </Paragraph>

      <CalloutBox $type="critical">
        <strong>Is This a Real Incident?</strong>
        <ul>
          <li>Unexpected system outage or data loss</li>
          <li>Suspicious login activity or new user accounts</li>
          <li>Ransom message or extortion demand</li>
          <li>Customer data breach notification</li>
          <li>Malware detected by security software</li>
          <li>Unusual network traffic or system behavior</li>
          <li>Compromised email account or unauthorized access</li>
        </ul>
      </CalloutBox>

      <H2>Phase 1: Detection & Immediate Response (First 30 Minutes)</H2>

      <PhaseBox>
        <h3>First Steps</h3>
        <div className="phase-time">
          <FaClock /> 0–30 minutes
        </div>
        <ol>
          <li>
            <strong>Declare an incident:</strong> Alert leadership and your
            incident response team that a security event has occurred. Use your
            organization's escalation process.
          </li>
          <li>
            <strong>Activate your incident response plan:</strong> Gather your
            IR team. If you don't have a formal plan, assemble: IT lead,
            security point person, management, legal/compliance (if available).
          </li>
          <li>
            <strong>Isolate affected systems:</strong> Disconnect compromised
            devices from the network immediately. Don't shut down yet—this may
            destroy evidence needed later.
          </li>
          <li>
            <strong>Preserve evidence:</strong> Before making changes, take
            screenshots of any suspicious activity, error messages, or unusual
            behavior. Document everything with timestamps.
          </li>
          <li>
            <strong>Stop the spread:</strong> Reset passwords for administrative
            and sensitive accounts from a clean device. Consider forcing a
            re-login for all users.
          </li>
          <li>
            <strong>Notify key stakeholders silently:</strong> Inform CEO,
            board, legal, and insurance provider (if applicable). Agree on
            communication strategy before public statements.
          </li>
        </ol>
      </PhaseBox>

      <CalloutBox $type="warning">
        <strong>Don't Panic—Think Strategically</strong>
        <ul>
          <li>
            Don't immediately shut down systems—this destroys forensic evidence
          </li>
          <li>
            Don't delete suspicious files or clear logs—forensics teams need
            them
          </li>
          <li>Don't communicate publicly until you understand the scope</li>
          <li>Don't assume it's a small incident—assume worst-case and adjust</li>
        </ul>
      </CalloutBox>

      <H2>Phase 2: Assessment & Containment (30 Minutes – 24 Hours)</H2>

      <PhaseBox>
        <h3>Understand What Happened</h3>
        <div className="phase-time">
          <FaClock /> 30 min–24 hours
        </div>
        <ol>
          <li>
            <strong>Assess scope and severity:</strong> How many systems are
            affected? What data is at risk? Is this ransomware, data theft,
            availability loss, or something else?
          </li>
          <li>
            <strong>Check backups:</strong> Test that backups are accessible and
            uninfected. Ransomware often targets backup systems.
          </li>
          <li>
            <strong>Review access logs:</strong> Look for unauthorized logins,
            new accounts, or privilege escalation. Check both on-premises and
            cloud systems (email, storage, etc.).
          </li>
          <li>
            <strong>Quarantine suspicious files:</strong> Move malware or
            suspicious files to an isolated location for analysis (don't delete
            yet).
          </li>
          <li>
            <strong>Engage external help if needed:</strong> Contact your
            incident response firm, managed security provider, or law
            enforcement if the incident is severe, involves ransomware, or
            affects multiple organizations.
          </li>
          <li>
            <strong>Determine if data was exfiltrated:</strong> Did the attacker
            copy data outside your network? Check outbound traffic logs and
            consider whether regulatory notifications are required.
          </li>
        </ol>
      </PhaseBox>

      <H3>Containment Actions</H3>

      <CalloutBox $type="success">
        <strong>Stop Further Damage</strong>
        <ul>
          <li>
            Change all passwords (especially admin accounts) from a clean device
          </li>
          <li>
            Block malicious IP addresses and domains at your firewall and email
            gateway
          </li>
          <li>
            Disable or reset any compromised user accounts
          </li>
          <li>
            Enable multi-factor authentication (2FA) company-wide if not already
            in place
          </li>
          <li>
            Patch known vulnerabilities that may have been exploited
          </li>
          <li>
            Monitor for lateral movement—watch for signs the attacker is moving
            to other systems
          </li>
          <li>
            Preserve forensic data before systems are wiped or restored
          </li>
        </ul>
      </CalloutBox>

      <H2>Phase 3: Communication & Notification (Concurrent)</H2>

      <PhaseBox>
        <h3>Who to Notify</h3>
        <div className="phase-time">
          <FaClock /> Ongoing, timeline depends on incident type
        </div>
        <ol>
          <li>
            <strong>Internal communication:</strong> Brief staff on what
            happened and what actions to take (password changes, monitoring for
            phishing, etc.). Be honest but avoid spreading panic.
          </li>
          <li>
            <strong>Customer notification:</strong> If customer data was
            compromised, determine notification timeline based on regulations
            (often 30–60 days). Work with legal and communications teams.
          </li>
          <li>
            <strong>Regulatory notification:</strong> Depending on your industry
            (healthcare, finance, etc.), you may need to notify regulators,
            state attorneys general, or data protection authorities.
          </li>
          <li>
            <strong>Law enforcement:</strong> Contact FBI, Secret Service (for
            financial crimes), or local police if the incident involves illegal
            activity or extortion.
          </li>
          <li>
            <strong>Business partners and vendors:</strong> Notify anyone who
            has access to your systems or whose data you process.
          </li>
          <li>
            <strong>Public statement:</strong> If the incident is large or will
            be public, prepare a statement acknowledging the incident,
            confirming response actions, and directing people to updates.
          </li>
        </ol>
      </PhaseBox>

      <H3>Communication Template</H3>

      <CalloutBox $type="tip">
        <strong>Key Message Points</strong>
        <ul>
          <li>
            What happened (general description, avoid technical jargon)
          </li>
          <li>
            When it was discovered
          </li>
          <li>
            What data is affected (be specific)
          </li>
          <li>
            What actions are being taken (immediate containment and investigation)
          </li>
          <li>
            What people should do now (change passwords, monitor accounts, etc.)
          </li>
          <li>
            How to get more information or report concerns
          </li>
        </ul>
      </CalloutBox>

      <H2>Phase 4: Recovery & Investigation (24 Hours – Weeks)</H2>

      <PhaseBox>
        <h3>Restore Systems & Investigate</h3>
        <div className="phase-time">
          <FaClock /> 1–4 weeks (longer for complex incidents)
        </div>
        <ol>
          <li>
            <strong>Conduct forensic analysis:</strong> Work with your IR firm
            or forensic team to determine how the attack happened, what was
            accessed, and how to prevent recurrence.
          </li>
          <li>
            <strong>Restore from clean backups:</strong> Only restore systems
            after you're confident you've removed the attacker's access and
            tools.
          </li>
          <li>
            <strong>Patch and harden systems:</strong> Apply security patches,
            disable unnecessary services, and improve firewall rules.
          </li>
          <li>
            <strong>Monitor for re-infection:</strong> Watch for signs the
            attacker is returning or has backdoors. Monitor for 30+ days.
          </li>
          <li>
            <strong>Conduct internal review:</strong> Determine what security
            gaps allowed this incident. Plan improvements.
          </li>
          <li>
            <strong>Document lessons learned:</strong> Create an incident report
            documenting timeline, response effectiveness, and improvements needed.
          </li>
        </ol>
      </PhaseBox>

      <H2>Key Contact Information (Fill in Now)</H2>

      <CalloutBox $type="tip">
        <strong>Keep This Information Current</strong>
        <ul>
          <li>
            <strong>Incident Response Lead:</strong> [Name, Phone, Email]
          </li>
          <li>
            <strong>IT Director/Security Lead:</strong> [Name, Phone, Email]
          </li>
          <li>
            <strong>Legal/Compliance Contact:</strong> [Name, Phone, Email]
          </li>
          <li>
            <strong>CEO/Board Contact:</strong> [Name, Phone, Email]
          </li>
          <li>
            <strong>Insurance Provider:</strong> [Phone, Policy #]
          </li>
          <li>
            <strong>External IR Firm:</strong> [Company Name, 24/7 Phone]
          </li>
          <li>
            <strong>Managed Security Provider:</strong> [Company Name, Phone]
          </li>
          <li>
            <strong>FBI Local Field Office:</strong> [Phone Number]
          </li>
        </ul>
      </CalloutBox>

      <H2>Before the Next Incident: Preparation</H2>

      <Paragraph>
        The best time to prepare for an incident is before it happens. Make sure
        you have:
      </Paragraph>

      <ul>
        <li>
          <strong>A written incident response plan</strong> that covers roles,
          responsibilities, and escalation procedures
        </li>
        <li>
          <strong>Contact list</strong> with key stakeholders, vendors, and law
          enforcement (update quarterly)
        </li>
        <li>
          <strong>Backup and recovery procedures</strong> that are tested monthly
        </li>
        <li>
          <strong>Security monitoring</strong> (SIEM, log aggregation, or
          endpoint detection) so you catch incidents early
        </li>
        <li>
          <strong>Incident response tabletop exercises</strong> at least annually
          so your team knows what to do
        </li>
        <li>
          <strong>Cyber insurance</strong> that covers incident response and
          notification costs
        </li>
      </ul>

      <H2>Related Resources</H2>
      <ul>
        <li>
          <a href={href(LINKS.CYBERSECURITY_FRAMEWORK.DATA_BREACH_RESPONSE_AND_COMPLIANCE)}>
            Data Breach Response and Compliance
          </a>
        </li>
        <li>
          <a href={href(LINKS.ARTICLE.INCIDENT_RESPONSE_PLAN)}>
            Incident Response Plan Article
          </a>
        </li>
        <li>
          <a href={href(LINKS.CYBERSECURITY_FRAMEWORK.BCDR_BASICS)}>
            BCDR Basics: Backup and Disaster Recovery
          </a>
        </li>
        <li>
          <a href={href(LINKS.BEST_PRACTICES.PATCH_MANAGEMENT_STRATEGY)}>
            Patch Management Strategy
          </a>
        </li>
      </ul>

      <ButtonRow data-print="hide">
        <Button
          as="button"
          type="button"
          onClick={() => window.print()}
          style={{ fontFamily: "inherit" }}
        >
          <FaPrint /> Print or save as PDF
        </Button>
        <Button
          href="https://www.facebook.com/sharer/sharer.php?u=https://csfsafe.com/resources/quick-guides/incident-response"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaShare /> Share Guide
        </Button>
      </ButtonRow>
      </ContentLayout>
  );
};

IncidentResponseGuide.propTypes = {
  istouch: PropTypes.bool.isRequired,
  isdarkmode: PropTypes.bool.isRequired,
};

export default IncidentResponseGuide;
